/**
 * PostExtras.jsx — quoted posts and polls.
 *
 * Both live outside PostCard because both are self-contained, both are already
 * long enough to bury the card's own logic, and both are reused by the post
 * detail page.
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Check, Clock, Link as LinkIcon } from 'lucide-react';
import { Avatar, Button } from './ui';
import { VerifiedBadge } from './Verification';
import { timeAgo, plural } from '../lib/format';
import { confirmWrite, QUEUED_MESSAGE } from '../lib/write';

import SafeImage from './SafeImage';
/* ── A quoted post ─────────────────────────────────────────────────────────── */

/**
 * Rendered from the snapshot stored on the quoting post, never from a live
 * read of the original.
 *
 * That is deliberate. It survives the original being deleted, which is the
 * honest outcome — you commented on something someone said, and they should
 * not be able to erase what you responded to and leave your words hanging with
 * no context. It also means a feed of fifty quotes costs no extra reads.
 *
 * The trade is that a quote shows the text as it was when quoted. If the author
 * later edits, the quote does not follow. That is arguably the more truthful
 * behaviour anyway.
 */
export function QuotedPost({ quoted }) {
  if (!quoted) return null;

  const authorPath = quoted.authorUsername ? `/u/${quoted.authorUsername}` : null;
  const postPath = quoted.postId ? `/post/${quoted.postId}` : null;

  const body = (
    <>
      <div className="quoted__head">
        <Avatar
          profile={{
            uid: quoted.postId,
            displayName: quoted.authorDisplayName,
            avatarUrl: quoted.authorAvatarUrl,
            avatarColour: quoted.authorAvatarColour,
          }}
          size={22}
        />
        <span className="quoted__name">{quoted.authorDisplayName || 'Clincordant member'}</span>
        {quoted.authorUsername && (
          <span className="quoted__handle">@{quoted.authorUsername}</span>
        )}
        <VerifiedBadge tier={quoted.authorTier ?? 'unverified'} />
        {quoted.createdAt && (
          <>
            <span aria-hidden="true">·</span>
            <span className="quoted__time">{timeAgo(quoted.createdAt)}</span>
          </>
        )}
      </div>

      {/* An article is quoted by its headline, never its body. The snapshot
          deliberately carries no text for one — 25,000 characters inside a
          quote card is not a quote, and the security rule that pins a snapshot
          to the real post cannot match a truncated copy. See usePosts.repost. */}
      {quoted.title
        ? <p className="quoted__title">{quoted.title}</p>
        : quoted.text && <p className="quoted__text">{quoted.text}</p>}

      {quoted.media?.[0]?.url && (
        <SafeImage
          className="quoted__media"
          src={quoted.media[0].url}
          alt={quoted.media[0].alt || ''}
          loading="lazy"
          decoding="async"
        />
      )}
    </>
  );

  // Not a Link when there is no destination — a dead link that looks live is
  // worse than plain text.
  return postPath
    ? <Link to={postPath} className="quoted">{body}</Link>
    : <div className="quoted quoted--orphan">{body}</div>;
}

/* ── Link preview ──────────────────────────────────────────────────────────── */

/**
 * The card a shared link unfurls into.
 *
 * On a clinical platform links are usually the point of the post — a paper, a
 * guideline, a safety alert — and a bare URL asks the reader to tap before they
 * know whether it is worth it. A title and a source is what turns "someone
 * posted a link" into "someone posted the new NICE guidance".
 *
 * DESIGN DECISIONS
 *
 * The domain is always shown, and never styled to look endorsed.
 *   The preview is written by the page being linked to. Its title and image are
 *   whatever that site chose to put in its meta tags, which on a platform where
 *   clinical credibility matters means the domain has to be visible so a reader
 *   can weigh it themselves. A beautiful card with no source is how a blog post
 *   gets mistaken for a journal.
 *
 * The image is hotlinked and allowed to fail.
 *   It is the publisher's asset on the publisher's CDN, which is what a
 *   preview image is for. It can also 404 or change, so a broken one removes
 *   the frame instead of leaving a grey box.
 *
 * Not a nested <a>.
 *   The whole post card is already a link. An anchor inside an anchor is
 *   invalid and browsers recover from it unpredictably, so this is a div that
 *   opens the URL itself and stops the event travelling up to the card.
 */
export function LinkPreview({ preview }) {
  const [broken, setBroken] = useState(false);
  if (!preview?.url || !preview.title) return null;

  const open = (e) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(preview.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`lpreview${preview.image && !broken ? '' : ' lpreview--noimg'}`}
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') open(e); }}
      aria-label={`${preview.title} — opens ${preview.siteName} in a new tab`}
    >
      {preview.image && !broken && (
        <img
          className="lpreview__img"
          src={preview.image}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
        />
      )}

      <div className="lpreview__body">
        <span className="lpreview__site">
          <LinkIcon size={11} aria-hidden="true" />
          {preview.siteName}
        </span>
        <span className="lpreview__title">{preview.title}</span>
        {preview.description && (
          <span className="lpreview__desc">{preview.description}</span>
        )}
      </div>
    </div>
  );
}

/* ── Poll ──────────────────────────────────────────────────────────────────── */

/**
 * Results are hidden until you have voted or the poll has closed.
 *
 * Showing a running tally to someone who has not yet answered turns a poll into
 * a conformity test — most people pick the leading option, and on a clinical
 * question that is actively harmful, because what you want to know is what
 * people genuinely do, not what they think their colleagues approve of.
 */
export function Poll({ poll, counts = [], totalVotes = 0, myVote, onVote, onClearVote }) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  // Why a vote did not go through. Shown in the poll rather than as a toast —
  // a toast that appears over the feed leaves you looking at an unchanged poll
  // with no idea which one it referred to.
  const [problem, setProblem] = useState(null);
  // Lets someone who has voted go back to the options without withdrawing
  // first, so changing a mis-tap is one step rather than two.
  const [editing, setEditing] = useState(false);

  const closesAt = poll.closesAt?.toMillis?.() ?? poll.closesAt ?? 0;
  const closed = closesAt > 0 && Date.now() > closesAt;
  const voted = Array.isArray(myVote) && myVote.length > 0;
  const showResults = (voted && !editing) || closed;

  // Seed the picker with what they chose, so "change" starts from their answer
  // rather than from nothing.
  useEffect(() => {
    if (editing && voted) setPicked(myVote);
  }, [editing, voted, myVote]);

  const pct = useMemo(() => {
    const total = counts.reduce((a, b) => a + (b || 0), 0) || 0;
    return counts.map((c) => (total ? Math.round(((c || 0) / total) * 100) : 0));
  }, [counts]);

  const leader = useMemo(
    () => (showResults ? pct.indexOf(Math.max(...pct)) : -1),
    [pct, showResults]
  );

  const toggle = (i) => {
    if (poll.multiple) {
      setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
    } else {
      setPicked([i]);
    }
  };

  const submit = async () => {
    if (!picked.length || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      /**
       * Bounded, because an unacknowledged Firestore write never settles.
       *
       * This is why voting "left it loading": the promise from setDoc resolves
       * on SERVER acknowledgement, and a write that cannot reach the server
       * stays pending forever — so neither the try, the catch nor the finally
       * ever ran and the spinner stayed up until the page was reloaded.
       */
      const outcome = await confirmWrite(onVote(picked));
      setEditing(false);
      if (outcome === 'queued') setProblem(QUEUED_MESSAGE);
    } catch (err) {
      console.error('Vote failed', err);
      setProblem(
        err?.code === 'permission-denied'
          ? 'This poll is not accepting votes from your account.'
          : 'Your vote did not save. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onClearVote?.();
      setPicked([]);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="poll" onClick={(e) => e.stopPropagation()}>
      {/* Optional. When the poll was posted with text above it, that text is
          the question and repeating it here would say the same thing twice. */}
      {poll.question?.trim() && <p className="poll__question">{poll.question}</p>}

      <ul className="poll__options">
        {poll.options.map((opt, i) => {
          const chosen = picked.includes(i);
          const mine = voted && myVote.includes(i);

          if (showResults) {
            return (
              <li key={i} className={`poll__result${mine ? ' is-mine' : ''}${i === leader ? ' is-leader' : ''}`}>
                <span className="poll__fill" style={{ width: `${pct[i]}%` }} aria-hidden="true" />
                <span className="poll__resultlabel">
                  {mine && <Check size={13} strokeWidth={3} aria-hidden="true" />}
                  {opt}
                </span>
                <span className="poll__pct">{pct[i]}%</span>
              </li>
            );
          }

          return (
            <li key={i}>
              <button
                type="button"
                className={`poll__option${chosen ? ' is-on' : ''}`}
                onClick={() => toggle(i)}
                aria-pressed={chosen}
              >
                <span className={`poll__box${poll.multiple ? '' : ' poll__box--radio'}`} aria-hidden="true">
                  {chosen && <Check size={12} strokeWidth={3} />}
                </span>
                {opt}
              </button>
            </li>
          );
        })}
      </ul>

      {!showResults && (
        <div className="poll__actions">
          <Button size="sm" onClick={submit} disabled={!picked.length || closed} loading={busy}>
            {voted ? 'Update vote' : 'Vote'}
          </Button>
          {voted && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Button>
          )}
        </div>
      )}

      {problem && (
        <p className="poll__problem" role="status">{problem}</p>
      )}

      {/* Changing a vote is only possible while the poll is open — after it
          closes the result is a record, not a live question. */}
      {showResults && voted && !closed && (
        <div className="poll__actions">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            Change vote
          </Button>
          <Button variant="ghost" size="sm" onClick={withdraw} loading={busy}>
            Withdraw
          </Button>
        </div>
      )}

      <p className="poll__meta">
        <BarChart3 size={13} aria-hidden="true" />
        {plural(totalVotes, 'vote', 'votes')}
        {closesAt > 0 && (
          <>
            <span aria-hidden="true">·</span>
            <Clock size={13} aria-hidden="true" />
            {closed ? 'Closed' : `Closes ${timeAgo(closesAt).replace(' ago', '')} from now`}
          </>
        )}
        {voted && !closed && <span className="poll__votedflag">You voted</span>}
      </p>
    </div>
  );
}

export const POST_EXTRAS_CSS = `
/* ── Quoted post ──────────────────────────────────────────────────────────── */
.quoted {
  display: block; position: relative; z-index: 1;
  margin-top: 12px; padding: 12px 14px;
  border: 1px solid var(--line-strong); border-radius: var(--radius);
  background: var(--bg-alt);
  color: inherit; text-decoration: none;
  transition: border-color var(--transition);
}
a.quoted:hover { border-color: var(--line-ui); }
.quoted--orphan { opacity: 0.85; }

.quoted__head {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  margin-bottom: 7px; font-size: var(--text-xs); color: var(--ink-faint);
}
.quoted__name { font-weight: 600; color: var(--ink); }
.quoted__text {
  margin: 0; font-size: var(--text-sm); line-height: 1.55;
  white-space: pre-wrap;
  display: -webkit-box; -webkit-line-clamp: 6; -webkit-box-orient: vertical;
  overflow: hidden;
}
/* A quoted article shows its headline, set like a headline. */
.quoted__title {
  margin: 0; font-size: var(--text-sm); font-weight: 700; line-height: 1.4;
  color: var(--ink); overflow-wrap: anywhere;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden;
}
.quoted__media {
  margin-top: 8px; width: 100%; max-height: 220px;
  object-fit: cover; border-radius: var(--radius-sm); display: block;
}

/* ── Link preview ─────────────────────────────────────────────────────────── */
.lpreview {
  position: relative; z-index: 1;
  display: flex; flex-direction: column;
  margin-top: 12px; overflow: hidden;
  border: 1px solid var(--line-strong); border-radius: var(--radius);
  background: var(--bg-alt);
  cursor: pointer;
  transition: border-color var(--transition), background var(--transition);
}
.lpreview:hover { border-color: var(--line-ui); background: var(--card-hover); }
.lpreview:focus-visible { box-shadow: var(--focus-ring); outline: none; }

.lpreview__img {
  width: 100%; max-height: 220px; object-fit: cover;
  display: block; background: var(--bg-alt);
  border-bottom: 1px solid var(--line);
}

.lpreview__body { display: flex; flex-direction: column; gap: 3px; padding: 11px 13px; min-width: 0; }

/* The domain, first and always. A card with no visible source is how a blog
   post gets read as a journal. */
.lpreview__site {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.02em;
  color: var(--ink-faint); text-transform: lowercase;
}
.lpreview__title {
  font-size: var(--text-sm); font-weight: 650; line-height: 1.4; color: var(--ink);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.lpreview__desc {
  font-size: var(--text-xs); line-height: 1.5; color: var(--ink-soft);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

/* With no image the card is a compact strip rather than a tall empty box. */
.lpreview--noimg .lpreview__body { padding: 10px 13px; }

/* ── Poll ─────────────────────────────────────────────────────────────────── */
.poll {
  position: relative; z-index: 1;
  margin-top: 12px; padding: 14px;
  border: 1px solid var(--line-strong); border-radius: var(--radius);
}
.poll__question { margin: 0 0 12px; font-weight: 600; font-size: var(--text-sm); line-height: 1.5; }
.poll__options { list-style: none; margin: 0 0 10px; padding: 0; display: flex; flex-direction: column; gap: 7px; }

.poll__option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 11px 14px; min-height: var(--tap);
  border: 1px solid var(--line-strong); border-radius: var(--radius-full);
  background: var(--card); color: var(--ink);
  font: inherit; font-size: var(--text-sm); text-align: left; cursor: pointer;
  transition: border-color var(--transition), background var(--transition);
}
.poll__option:hover { border-color: var(--line-ui); }
.poll__option.is-on { border-color: var(--primary); background: var(--primary-tint); font-weight: 600; }
.poll__box {
  width: 18px; height: 18px; flex: 0 0 auto;
  border: 1.5px solid var(--line-strong); border-radius: 4px;
  display: grid; place-items: center; color: var(--primary);
}
.poll__box--radio { border-radius: 50%; }
.poll__option.is-on .poll__box { border-color: var(--primary); }

/* Results */
.poll__result {
  position: relative; overflow: hidden;
  display: flex; align-items: center; gap: 8px;
  padding: 11px 14px; min-height: var(--tap);
  border: 1px solid var(--line-strong); border-radius: var(--radius-full);
  font-size: var(--text-sm);
}
.poll__result.is-leader { border-color: var(--primary); }
.poll__result.is-mine { font-weight: 600; }
.poll__fill {
  position: absolute; inset: 0 auto 0 0;
  background: var(--primary-tint);
  transition: width 420ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.poll__resultlabel {
  position: relative; display: flex; align-items: center; gap: 6px;
  flex: 1; min-width: 0;
}
.poll__pct { position: relative; font-variant-numeric: tabular-nums; color: var(--ink-soft); }

.poll__actions { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }

.poll__problem {
  margin: 0 0 10px;
  font-size: var(--text-xs); line-height: 1.5; color: var(--danger);
}

.poll__meta {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  margin: 0; font-size: var(--text-xs); color: var(--ink-faint);
}
.poll__votedflag {
  margin-left: auto; padding: 2px 8px;
  background: var(--primary-tint); color: var(--primary-text);
  border-radius: var(--radius-full); font-weight: 600;
  border: 1px solid color-mix(in srgb, var(--primary) 30%, var(--line-strong));
}

@media (prefers-reduced-motion: reduce) {
  .poll__fill { transition: none; }
}
`;
